/**
 * Список экрана «Возражения» (#846) — взгляд, причины и пометки о неполноте
 * счёта — над запросом, который ему отдали: экран «Возражения» (`ObjectionsView`)
 * и вкладка «Продажи» «Голоса клиента» (#2060) спрашивают поток сами, со своим
 * окном периода и своей полосой фильтра.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { DashboardPanel } from '@/components/dashboard/dashboard-panel';
import { Button } from '@/components/ui/button';
import { SegmentedButtons } from '@/components/ui/segmented-buttons';
import { useOpenRecord } from '@/components/dashboards/shared/highlights/useOpenRecord';
import { ObjectionCodeCard } from './ObjectionCodeCard';
import { orderCodes, percent } from './objection-view-model';
import type { ObjectionsSearch } from './objections-search';
import type { useObjectionStream } from './useObjectionStream';
import { VoiceTopList } from '../voice/VoiceTopList';
import { VoiceSeriesNote } from '../voice/VoiceSeriesNote';
import { useVoiceBlockSeries } from '../voice/useVoiceSeries';
import { objectionSeriesRef } from '../voice/voice-series-view-model';
import { useVoiceComparable } from '../voice/voice-theme-filter-context';

interface ObjectionsListProps {
  stream: ReturnType<typeof useObjectionStream>['query'];
  search: ObjectionsSearch;
  onSearchChange: (patch: Partial<ObjectionsSearch>) => void;
  /** The first codes only, then «Show all N» — the Sales section (#2060). */
  limit?: number;
  /** The stream asked the rule Р3 marks (the Sales section, #2698). */
  marked?: boolean;
}

export function ObjectionsList({
  stream,
  search,
  onSearchChange,
  limit,
  marked = false,
}: ObjectionsListProps) {
  const { t } = useTranslation(['dashboard', 'records', 'common']);
  // A theme filter without a comparable previous window (#2229): no previous
  // window to speak of — the server did not read it.
  const comparable = useVoiceComparable();
  const openRecord = useOpenRecord();
  const mode = search.view ?? 'market';
  const expanded = search.code ?? null;

  const data = stream.data;
  const codes = data ? orderCodes(data.codes, mode) : [];
  const openCall = (recordId: string, quote: string) => openRecord(recordId, { quote });
  // Inside the Voice section (#2394 §9 п. 7) each code gets its series; the
  // Objections screen has no series scope and asks nothing. Only the market
  // view draws it: the team view counts unhandled objections, not mentions.
  // Only the codes on screen: those under «Show all» are asked when shown.
  const [shownCodes, setShownCodes] = useState<readonly string[]>([]);
  const series = useVoiceBlockSeries(
    mode === 'market' ? codes.filter((code) => shownCodes.includes(code.code)) : [],
    (code) => ({
      ref: objectionSeriesRef(code.code),
      calls: code.calls,
      prevCalls: code.prevCalls ?? 0,
    })
  );

  return (
    <div className="space-y-4">
      <SegmentedButtons
        ariaLabel={t('objectionsPage.modeLabel')}
        options={(['market', 'team'] as const).map((id) => ({
          id,
          label: t(`objectionsPage.mode.${id}`),
        }))}
        value={mode}
        onChange={(id) => onSearchChange({ view: id === 'market' ? undefined : id })}
      />
      <p className="text-sm text-fg-muted">{t(`objectionsPage.modeHint.${mode}`)}</p>

      <DashboardPanel
        title={t('objectionsPage.listTitle')}
        action={
          data ? (
            <span className="text-xs text-fg-muted">
              {t('objectionsPage.summary', {
                calls: data.callsWithObjections,
                count: data.callsInWindow,
                share: percent(data.callsWithObjections, data.callsInWindow),
              })}
            </span>
          ) : undefined
        }
      >
        {stream.isPending ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-fg-muted" />
          </div>
        ) : stream.isError ? (
          <div className="py-8 text-center text-sm text-fg-muted">
            <p>{t('objectionsPage.error')}</p>
            <Button variant="ghost" size="sm" onClick={() => void stream.refetch()}>
              {t('common:retry')}
            </Button>
          </div>
        ) : codes.length === 0 ? (
          <p className="py-8 text-center text-sm text-fg-muted">
            {data && data.unmappedShare > 0
              ? t('objectionsPage.emptyUnrecognized')
              : t('objectionsPage.empty')}
          </p>
        ) : (
          <>
            {/* The sparkline is the market view's: the team view counts unhandled. */}
            {mode === 'market' && (
              <VoiceSeriesNote
                block={series.block}
                baseNote={t('voicePage.series.objectionsBase')}
                lowerBound={data?.truncated}
              />
            )}
            <VoiceTopList
              rows={codes}
              limit={limit}
              rowKey={(code) => code.code}
              calls={(code) => code.calls}
              revealKeys={[expanded]}
              onShownKeys={setShownCodes}
              renderRow={(code) => (
                <ObjectionCodeCard
                  key={code.code}
                  code={code}
                  mode={mode}
                  trendBase={{
                    recognizedCalls: data?.recognizedCalls ?? 0,
                    prevRecognizedCalls: data?.prevRecognizedCalls ?? null,
                    prevHasUnrecognized: data?.prevHasUnrecognized ?? null,
                    prevTruncated: data?.prevTruncated ?? false,
                  }}
                  expanded={expanded === code.code}
                  onToggle={() =>
                    onSearchChange({
                      code: expanded === code.code ? undefined : code.code,
                    })
                  }
                  onOpenCall={openCall}
                  series={series.trendOf(code)}
                  marked={marked}
                />
              )}
            />
          </>
        )}

        {data && (
          <div className="mt-3 space-y-1 text-xs text-fg-muted">
            {data.unmappedShare > 0 && (
              <p>
                {t('supervisorDashboard.quality.objections.unmapped', {
                  share: Math.round(data.unmappedShare * 100),
                })}
              </p>
            )}
            {data.otherShare > 0 && (
              <p>
                {t('supervisorDashboard.quality.objections.other', {
                  share: Math.round(data.otherShare * 100),
                })}
              </p>
            )}
            {data.truncated && (
              <p>{t('supervisorDashboard.quality.objections.truncated')}</p>
            )}
            {comparable && mode === 'market' && data.prevTruncated && (
              <p>{t('objectionsPage.prevTruncated')}</p>
            )}
            {comparable && mode === 'market' && data.prevCallsWithObjections !== null && (
              <p>
                {t('objectionsPage.prevWindow', {
                  count: data.prevCallsWithObjections,
                })}
              </p>
            )}
          </div>
        )}
      </DashboardPanel>
    </div>
  );
}
